
import type React from "react"
import { createContext, useContext, useState } from "react"

interface Appointment {
  id: string
  trainerId: string
  userId: string
  date: string
  startTime: string
  endTime: string
  status: "scheduled" | "completed" | "cancelled"
  notes?: string
  type: string
}

interface TrainerContextType {
  appointments: Appointment[]
  addAppointment: (appointment: Omit<Appointment, "id">) => void
  updateAppointment: (id: string, appointment: Partial<Appointment>) => void
  deleteAppointment: (id: string) => void
  getTrainerAppointments: (trainerId: string) => Appointment[]
  getUserAppointments: (userId: string) => Appointment[]
}

const TrainerContext = createContext<TrainerContextType | undefined>(undefined)

// Mock data for initial appointments
const initialAppointments: Appointment[] = [
  {
    id: "1",
    trainerId: "1",
    userId: "1",
    date: "2023-05-15",
    startTime: "10:00",
    endTime: "11:00",
    status: "scheduled",
    notes: "Focus on upper body strength",
    type: "Strength Training",
  },
  {
    id: "2",
    trainerId: "2",
    userId: "1",
    date: "2023-05-18",
    startTime: "14:00",
    endTime: "15:00",
    status: "scheduled",
    notes: "Cardio session",
    type: "Cardio",
  },
  {
    id: "3",
    trainerId: "1",
    userId: "2",
    date: "2023-05-16",
    startTime: "11:00",
    endTime: "12:00",
    status: "scheduled",
    notes: "Flexibility and mobility",
    type: "Flexibility",
  },
]

export function TrainerProvider({ children }: { children: React.ReactNode }) {
  const [appointments, setAppointments] = useState<Appointment[]>(initialAppointments)

  const addAppointment = (appointment: Omit<Appointment, "id">) => {
    const newAppointment = {
      ...appointment,
      id: Math.random().toString(36).substring(2, 9),
    }
    setAppointments((prev) => [...prev, newAppointment])
  }

  const updateAppointment = (id: string, updatedFields: Partial<Appointment>) => {
    setAppointments((prev) =>
      prev.map((appointment) => (appointment.id === id ? { ...appointment, ...updatedFields } : appointment)),
    )
  }

  const deleteAppointment = (id: string) => {
    setAppointments((prev) => prev.filter((appointment) => appointment.id !== id))
  }

  const getTrainerAppointments = (trainerId: string) => {
    return appointments.filter((appointment) => appointment.trainerId === trainerId)
  }

  const getUserAppointments = (userId: string) => {
    return appointments.filter((appointment) => appointment.userId === userId)
  }

  return (
    <TrainerContext.Provider
      value={{
        appointments,
        addAppointment,
        updateAppointment,
        deleteAppointment,
        getTrainerAppointments,
        getUserAppointments,
      }}
    >
      {children}
    </TrainerContext.Provider>
  )
}

export function useTrainer() {
  const context = useContext(TrainerContext)
  if (context === undefined) {
    throw new Error("useTrainer must be used within a TrainerProvider")
  }
  return context
}
